import { useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { OtpInput } from '@components/OtpInput';
import { HeroHeading } from '@components/medicus-ui/HeroHeading';
import { useAuthStore } from '@lib/store/auth.store';
import { capitalizeName } from '@lib/utils/text';
import {
  buscarLegajo,
  solicitarOtp,
  validarOtp,
  reportarContactoAlternativo,
  type BuscarLegajoResultado,
  type MetodoOtp,
  type MetodoOtpInfo,
} from '@lib/services/portal.service';

type Step = 'dni' | 'metodo' | 'otp';
type DniStatus = 'idle' | 'searching' | 'not-found';

function normalizeDni(value: string): string {
  return value.replace(/\D/g, '').slice(0, 9);
}

function isValidDni(dni: string): boolean {
  // Sin tope mínimo de largo: hay DNIs viejos cortos (4-6 dígitos) en los
  // padrones. `normalizeDni` ya descarta no-dígitos y corta a 9.
  return /^\d{3,}$/.test(dni);
}

function extractErrorMessage(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { error?: { message?: string } } } };
  return e.response?.data?.error?.message ?? fallback;
}

/**
 * Mapea el error del POST `/portal/otp/validar` a un mensaje legible.
 *
 * Reglas:
 *  - HTTP 5xx o sin response       → "Error de servidor, volvé más tarde."
 *  - Mensaje empieza con "Demasiad" → bloqueado por máximo de intentos.
 *  - Cualquier otro 4xx            → preferimos mostrar el mensaje del
 *                                    server (que ya viene amigable, ej.
 *                                    "Código incorrecto. Te quedan N
 *                                    intentos.") y si no hay, fallback a
 *                                    "Código inválido."
 *
 * NO matcheamos por la palabra "intentos" suelta — el mensaje normal de
 * código incorrecto la contiene ("Te quedan N intentos.") y antes caía
 * en la rama de "demasiados intentos", mostrando al titular el cartel
 * de bloqueo aunque le quedaran intentos disponibles.
 */
function mapearErrorOtp(err: unknown): string {
  const e = err as {
    response?: { status?: number; data?: { error?: { message?: string } } };
  };
  const status = e.response?.status;
  if (!status || status >= 500) {
    return 'Error de servidor, volvé más tarde.';
  }
  const msgRaw = e.response?.data?.error?.message ?? '';
  if (msgRaw.toLowerCase().startsWith('demasiad')) {
    return 'Intentaste demasiadas veces. Pedí un código nuevo y volvé en unos minutos.';
  }
  return msgRaw.trim() || 'Código inválido.';
}

export function LandingPage() {
  const { token } = useAuthStore();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('dni');
  const [dni, setDni] = useState('');
  const [dniStatus, setDniStatus] = useState<DniStatus>('idle');
  const [dniError, setDniError] = useState<string | null>(null);
  const [resultado, setResultado] = useState<BuscarLegajoResultado | null>(null);

  const [metodoSel, setMetodoSel] = useState<MetodoOtp | null>(null);
  const [metodoLoading, setMetodoLoading] = useState(false);
  const [metodoError, setMetodoError] = useState<string | null>(null);

  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState<string | null>(null);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpInfo, setOtpInfo] = useState<string | null>(null);

  if (token) return <Navigate to="/dashboard" replace />;

  async function handleDniSubmit(e: FormEvent) {
    e.preventDefault();
    setDniError(null);
    setResultado(null);
    const clean = dni.trim();
    if (!isValidDni(clean)) {
      setDniError('Ingresá un DNI válido (solo números, sin puntos).');
      return;
    }
    setDniStatus('searching');
    try {
      const r = await buscarLegajo(clean);
      setResultado(r);
      if (!r.found) {
        setDniStatus('not-found');
        return;
      }
      const metodos = r.metodos ?? [];
      if (metodos.length === 0) {
        // sin método para mostrar — enviamos directo
        await solicitarOtp(clean);
        setOtpInfo('Te enviamos un código de verificación (4 dígitos).');
        setStep('otp');
        return;
      }
      // pre-selecciono el primer método disponible
      setMetodoSel(metodos[0].tipo);
      setStep('metodo');
    } catch (err) {
      setDniStatus('not-found');
      setDniError(
        extractErrorMessage(err, 'No pudimos buscar tu legajo. Reintentá en unos segundos.'),
      );
    }
  }

  async function handleMetodoSubmit() {
    if (!metodoSel) return;
    setMetodoError(null);
    setMetodoLoading(true);
    try {
      await solicitarOtp(dni, metodoSel);
      const m = resultado?.metodos?.find((x) => x.tipo === metodoSel);
      const dest = m?.mask ? ` a ${m.mask}` : '';
      setOtpInfo(
        metodoSel === 'sms'
          ? `Te enviamos un SMS con el código${dest}.`
          : `Te enviamos un email con el código${dest}.`,
      );
      setStep('otp');
    } catch (err) {
      setMetodoError(
        extractErrorMessage(err, 'No pudimos enviarte el código. Reintentá.'),
      );
    } finally {
      setMetodoLoading(false);
    }
  }

  async function handleOtpComplete(code: string) {
    setOtpError(null);
    setOtpLoading(true);
    try {
      const r = await validarOtp(dni, code);
      if (r.ok) {
        // Redirect inmediato al onboarding, sin pantalla intermedia
        navigate(`/activar/${r.activacionToken}`, { replace: true });
      }
    } catch (err) {
      setOtpError(mapearErrorOtp(err));
      setOtp('');
    } finally {
      setOtpLoading(false);
    }
  }

  async function handleResendOtp() {
    setOtpError(null);
    setOtp('');
    setOtpLoading(true);
    try {
      await solicitarOtp(dni, metodoSel ?? undefined);
      setOtpInfo('Te enviamos un nuevo código.');
    } catch (err) {
      setOtpError(extractErrorMessage(err, 'No pudimos generar un nuevo código.'));
    } finally {
      setOtpLoading(false);
    }
  }

  return (
    <div className="landing">
      <main className="landing__main">
        {step === 'dni' && (
          <DniStep
            dni={dni}
            setDni={setDni}
            onSubmit={handleDniSubmit}
            status={dniStatus}
            error={dniError}
            resultado={resultado}
          />
        )}

        {step === 'metodo' && resultado?.found && (
          <MetodoStep
            primerNombre={resultado.primerNombre ?? resultado.titularNombre?.split(' ')[0] ?? ''}
            documento={dni}
            metodos={resultado.metodos ?? []}
            seleccionado={metodoSel}
            onSelect={setMetodoSel}
            onContinue={handleMetodoSubmit}
            onBack={() => {
              setStep('dni');
              setMetodoSel(null);
              setMetodoError(null);
            }}
            loading={metodoLoading}
            error={metodoError}
          />
        )}

        {step === 'otp' && resultado?.found && (
          <OtpStep
            titularNombre={resultado.titularNombre ?? ''}
            otp={otp}
            setOtp={setOtp}
            onComplete={handleOtpComplete}
            onResend={handleResendOtp}
            onBack={() => {
              setStep('metodo');
              setOtp('');
              setOtpError(null);
              setOtpInfo(null);
            }}
            loading={otpLoading}
            error={otpError}
            info={otpInfo}
          />
        )}

      </main>

      <footer className="landing__footer">
        <span className="landing__copyright">© Medicus S.A.</span>
        <Link to="/login" className="landing__adminLink">
          Acceso administradores
        </Link>
      </footer>
    </div>
  );
}

// ---------- Sub-componentes ----------

interface DniStepProps {
  dni: string;
  setDni: (s: string) => void;
  onSubmit: (e: FormEvent) => void;
  status: DniStatus;
  error: string | null;
  resultado: BuscarLegajoResultado | null;
}

function DniStep({ dni, setDni, onSubmit, status, error, resultado }: DniStepProps) {
  return (
    <>
      <section className="landing__hero">
        <HeroHeading>
          BIENVENIDO A <HeroHeading.HL>MEDICUS</HeroHeading.HL>
        </HeroHeading>
      </section>

      <section className="landing__card">
        <form onSubmit={onSubmit} noValidate>
          <label className="landing__field">
            <span className="landing__label">Ingresá tu número de DNI sin puntos ni espacios.</span>
            <input
              className="landing__input"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="off"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              enterKeyHint="go"
              maxLength={10}
              autoFocus
              placeholder="Ej: 30123456"
              value={dni}
              onChange={(e) => setDni(normalizeDni(e.target.value))}
              disabled={status === 'searching'}
            />
          </label>

          {error && (
            <p className="landing__error" role="alert">
              {error}
            </p>
          )}

          {status === 'not-found' && resultado && !resultado.found && (
            <div className="landing__info">
              {(() => {
                switch (resultado.motivo) {
                  case 'documento-no-titular':
                    return (
                      <>
                        <strong>Tu DNI figura como familiar, no como titular.</strong>
                        <p>
                          El alta la inicia el titular del legajo. Pedile a la
                          persona que es titular en tu organización que entre con su DNI.
                        </p>
                      </>
                    );
                  case 'padron-no-cerrado':
                    return (
                      <>
                        <strong>Tu padrón aún no está disponible.</strong>
                        <p>
                          RRHH todavía no terminó de cerrar la carga de tu organización.
                          Probá de vuelta en un rato.
                        </p>
                      </>
                    );
                  case 'legajo-vencido':
                    return (
                      <>
                        <strong>Tu link de activación venció.</strong>
                        <p>
                          Contactá a RRHH para que reactive tu legajo.
                        </p>
                      </>
                    );
                  case 'legajo-ya-validado':
                    return (
                      <>
                        <strong>Tu legajo ya fue validado.</strong>
                        <p>
                          Ya completaste el proceso de validación. Si necesitás
                          actualizar algún dato, contactá a tu RRHH.
                        </p>
                      </>
                    );
                  default:
                    return (
                      <>
                        <strong>No encontramos tu legajo todavía.</strong>
                        <p>
                          Verificá con el área de RRHH de tu organización que tu DNI esté
                          cargado en el padrón.
                        </p>
                      </>
                    );
                }
              })()}
            </div>
          )}

          <button
            type="submit"
            className="landing__submit"
            disabled={status === 'searching'}
          >
            {status === 'searching' ? (
              <span>Buscando tu legajo…</span>
            ) : (
              <span>Continuar</span>
            )}
          </button>
        </form>
      </section>
    </>
  );
}

interface MetodoStepProps {
  primerNombre: string;
  documento: string;
  metodos: MetodoOtpInfo[];
  seleccionado: MetodoOtp | null;
  onSelect: (m: MetodoOtp) => void;
  onContinue: () => void;
  onBack: () => void;
  loading: boolean;
  error: string | null;
}

/**
 * "Ninguno de estos contactos es mío": el padrón puede haber traído el mail o
 * el celular de otra persona, y en ese caso el titular NUNCA recibe el OTP y
 * queda afuera del onboarding sin salida. Acá deja el contacto correcto, que
 * queda como observación del legajo para que lo apruebe Medicus o su empresa.
 * No se le manda el código al contacto nuevo (ver el comentario de seguridad
 * en `PortalService.reportarContactoAlternativo`).
 */
function ContactoAlternativo({ documento }: { documento: string }) {
  const [abierto, setAbierto] = useState(false);
  const [tipo, setTipo] = useState<'email' | 'celular'>('email');
  const [valor, setValor] = useState('');
  const [estado, setEstado] = useState<'idle' | 'enviando' | 'ok'>('idle');
  const [error, setError] = useState<string | null>(null);

  async function enviar() {
    setError(null);
    setEstado('enviando');
    try {
      await reportarContactoAlternativo({ documento, tipo, valor: valor.trim() });
      setEstado('ok');
    } catch (err) {
      const e = err as { response?: { data?: { error?: { message?: string } } } };
      setError(e.response?.data?.error?.message ?? 'No pudimos registrar el contacto. Reintentá.');
      setEstado('idle');
    }
  }

  if (estado === 'ok') {
    return (
      <p className="status status-success" style={{ display: 'block', marginTop: 16, fontSize: 13 }}>
        Listo. Registramos tu contacto para que lo revise Medicus o tu organización. Cuando lo
        aprueben vas a poder recibir el código y continuar.
      </p>
    );
  }

  if (!abierto) {
    return (
      <button
        type="button"
        onClick={() => setAbierto(true)}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          marginTop: 16,
          fontSize: 13,
          color: 'var(--color-primary-darker)',
          fontWeight: 600,
          cursor: 'pointer',
          textDecoration: 'underline',
        }}
      >
        Ninguno de estos contactos es mío
      </button>
    );
  }

  return (
    <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--color-outline-sutil)' }}>
      <p className="text-element-mid" style={{ fontSize: 13, marginBottom: 10 }}>
        Dejanos un contacto tuyo. Lo tiene que aprobar Medicus o tu organización antes de que
        podamos enviarte el código.
      </p>
      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        {(['email', 'celular'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => {
              setTipo(t);
              setValor('');
            }}
            style={{
              flex: 1,
              padding: '8px 10px',
              fontSize: 13,
              borderRadius: 'var(--radius-md)',
              cursor: 'pointer',
              border: `1px solid ${tipo === t ? 'var(--color-primary-darker)' : 'var(--color-outline)'}`,
              background: tipo === t ? 'rgba(0,37,100,0.04)' : 'var(--color-background)',
              fontWeight: tipo === t ? 700 : 400,
            }}
          >
            {t === 'email' ? 'Email' : 'Celular'}
          </button>
        ))}
      </div>
      <input
        type={tipo === 'email' ? 'email' : 'tel'}
        value={valor}
        onChange={(e) => setValor(e.target.value)}
        placeholder={tipo === 'email' ? 'tunombre@mail.com' : 'Área + número, sin 0 ni 15'}
        disabled={estado === 'enviando'}
        style={{
          width: '100%',
          padding: '10px 12px',
          fontSize: 14,
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--color-outline)',
        }}
      />
      {error && (
        <p className="landing__error" role="alert" style={{ marginTop: 10 }}>
          {error}
        </p>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 12 }}>
        <button
          type="button"
          onClick={() => setAbierto(false)}
          disabled={estado === 'enviando'}
          style={{ background: 'none', border: 'none', fontSize: 13, color: '#94a3b8', cursor: 'pointer' }}
        >
          Cancelar
        </button>
        <button
          type="button"
          className="landing__submit"
          onClick={enviar}
          disabled={estado === 'enviando' || valor.trim().length < 5}
          style={{ width: 'auto', minWidth: 120 }}
        >
          {estado === 'enviando' ? 'Enviando…' : 'Enviar'}
        </button>
      </div>
    </div>
  );
}

function MetodoStep({
  primerNombre,
  documento,
  metodos,
  seleccionado,
  onSelect,
  onContinue,
  onBack,
  loading,
  error,
}: MetodoStepProps) {
  return (
    <>
      <section className="landing__hero">
        <h1 className="landing__title">¡Hola, {capitalizeName(primerNombre)}!</h1>
        <p className="landing__subtitle">
          ¿Qué método preferís para validar tus datos? Te enviaremos un código
          de 4 dígitos.
        </p>
      </section>

      <section className="landing__card">
        <fieldset
          style={{ border: 'none', padding: 0, margin: 0, display: 'grid', gap: 10 }}
        >
          <legend className="sr-only">Método de validación</legend>
          {metodos.map((m) => {
            const checked = seleccionado === m.tipo;
            return (
              <label
                key={m.tipo}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '14px 16px',
                  borderRadius: 'var(--radius-md)',
                  border: `1px solid ${checked ? 'var(--color-primary-darker)' : 'var(--color-outline)'}`,
                  background: checked ? 'rgba(0,37,100,0.04)' : 'var(--color-background)',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="radio"
                  name="metodo"
                  value={m.tipo}
                  checked={checked}
                  onChange={() => onSelect(m.tipo)}
                  disabled={loading}
                  style={{ accentColor: 'var(--color-primary-darker)' }}
                />
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <strong style={{ fontSize: 14 }}>
                    {m.tipo === 'sms' ? 'SMS al celular' : 'Email'}
                  </strong>
                  <span
                    style={{
                      fontSize: 13,
                      color: 'var(--color-element-mid)',
                      fontFamily: 'monospace',
                    }}
                  >
                    {m.mask}
                  </span>
                </div>
              </label>
            );
          })}
        </fieldset>

        {error && (
          <p className="landing__error" role="alert" style={{ marginTop: 12 }}>
            {error}
          </p>
        )}

        <ContactoAlternativo documento={documento} />

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: 22,
            gap: 12,
          }}
        >
          <button
            type="button"
            onClick={onBack}
            disabled={loading}
            style={{
              background: 'none',
              border: 'none',
              fontSize: 13,
              color: '#94a3b8',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <ArrowLeft size={14} strokeWidth={2.5} />
            Usar otro DNI
          </button>
          <button
            type="button"
            className="landing__submit"
            onClick={onContinue}
            disabled={loading || !seleccionado}
            style={{ width: 'auto', minWidth: 160 }}
          >
            {loading ? 'Enviando…' : 'Continuar'}
          </button>
        </div>
      </section>
    </>
  );
}

interface OtpStepProps {
  titularNombre: string;
  otp: string;
  setOtp: (v: string) => void;
  onComplete: (code: string) => void;
  onResend: () => void;
  onBack: () => void;
  loading: boolean;
  error: string | null;
  info: string | null;
}

function OtpStep({
  titularNombre,
  otp,
  setOtp,
  onComplete,
  onResend,
  onBack,
  loading,
  error,
  info,
}: OtpStepProps) {
  useEffect(() => {
    if (otp.length === 4 && !loading) onComplete(otp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp]);

  return (
    <>
      <section className="landing__hero">
        <h1 className="landing__title">¡Hola, {capitalizeName(titularNombre.split(' ')[0])}!</h1>
        <p className="landing__subtitle">
          Verificamos tu identidad antes de seguir.
          Ingresá el código de 4 dígitos que te enviamos.
        </p>
      </section>

      <section className="landing__card">
        <div style={{ textAlign: 'center' }}>
          <span className="landing__label">Código de verificación</span>
          <OtpInput
            length={4}
            value={otp}
            onChange={setOtp}
            onComplete={onComplete}
            autoFocus
            disabled={loading}
          />
          {info && !error && (
            <p className="text-element-mid" style={{ fontSize: 13, marginTop: 14 }}>
              {info}
            </p>
          )}
          {error && (
            <p className="landing__error" role="alert" style={{ marginTop: 14 }}>
              {error}
            </p>
          )}
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: 22,
            gap: 12,
          }}
        >
          <button
            type="button"
            onClick={onBack}
            disabled={loading}
            style={{
              background: 'none',
              border: 'none',
              fontSize: 13,
              color: '#94a3b8',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <ArrowLeft size={14} strokeWidth={2.5} />
            Usar otro DNI
          </button>
          <button
            type="button"
            onClick={onResend}
            disabled={loading}
            style={{
              background: 'none',
              border: 'none',
              fontSize: 13,
              color: 'var(--color-primary-darker)',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Reenviar código
          </button>
        </div>
      </section>
    </>
  );
}
